import Icon from "@/icons";
import Tippy from '@tippyjs/react';
import { Team, User } from "types";
import ErrorBox from "../Error/Box";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { CreateTeam } from "@/components/Dialogs/CreateTeam";
import { useEffect, useCallback } from "react";
import ErrorBoundary from "@/ErrorBoundary";
import { TeamContext } from "../ContextMenu/Team";
import { useQueryClient } from "@tanstack/react-query";
import logo from "../../../assets/images/logo.svg";
export default function TeamsSidebar({ socket, isOpen }: { socket: any, isOpen: any }) {
    const location = useLocation();
    const { teamId } = useParams();
    const navigate = useNavigate();
    const isActive = (path: string) => {
        if (location.pathname === path) return "bg-brand-green rounded-xl";
        else if (path.startsWith("/teams/") && location.pathname.startsWith(path)) return "bg-brand-green rounded-xl";
        else if (path === "/chat" && location.pathname.startsWith(path)) return "bg-brand-green rounded-xl";
        else return "bg-brand-back rounded-lg";
    };

    const queryClient = useQueryClient();
    const user = queryClient.getQueryState(["user"]) as any;
    const userteams = queryClient.getQueryState(["teams"]) as any;
    const unreadedMessages = queryClient.getQueryData(["unread"]) as {
        dmInfo: {
            [key: string]: {
                data: {
                    participants: {
                        [key: string]: User;
                    };
                };
                unread: number;
            };
        },
        teamInfo: {
            teamId: string;
            channelInfo: {
                [key: string]: {
                    unread: number;
                };
            };
            badgingInfo: {
                [key: string]: {
                    unread: number;
                };
            };
        }[];
    }

    const handleWheel = (e: any) => {
        e.preventDefault();
        const container = e.currentTarget;
        const containerScrollPosition = container.scrollLeft;
        container.scrollTo({
            top: 0,
            left: containerScrollPosition + e.deltaY,
            behaviour: 'smooth'
        });
    }


    const handleSocketMessage = useCallback(async (event: MessageEvent) => {
        const data = JSON.parse(event.data);
        try {
            if (data.t === "USER_JOINED_TEAM" && data.d.member?.id === user?.data?.user.id) {
                queryClient.invalidateQueries({ queryKey: ["teams"] });
            } else if (data.t === "USER_LEFT_TEAM" && data.d.member.id === user?.data?.user.id) {
                queryClient.invalidateQueries({ queryKey: ["teams"] });
                if (teamId === data.d.teamId) navigate("/");
            } else if (data.t === "TEAM_UPDATED") {
                queryClient.invalidateQueries({ queryKey: ["teams"] });
            } else if (data.t === "TEAM_DELETED") {
                queryClient.invalidateQueries({ queryKey: ["teams"] });
                if (teamId === data.d.teamId) navigate("/");
            } else if (data.t === "USER_LEFT_TEAM" && data.d.member?.id !== user?.data?.user.id) {
                queryClient.setQueryData(["team", data.d.teamId, "members"], (old: any) => {
                    return {
                        ...old,
                        members: old?.members?.filter((member: any) => member.id !== data.d.member.id)
                    };
                });
            } else if (data.t === "USER_JOINED_TEAM" && data.d.member?.id !== user?.data?.user.id) {
                queryClient.setQueryData(["team", data.d.teamId, "members"], (old: any) => {
                    data.d.member.roles = [];
                    return {
                        ...old,
                        members: [...(old?.members || []), data.d.member]
                    };
                });
            }
        } catch (error) {
            console.error(error);
        }
    }, [user?.data?.user]);

    useEffect(() => {
        if (!socket) return;

        socket.addEventListener("message", handleSocketMessage);

        return () => {
            socket.removeEventListener("message", handleSocketMessage);
        };
    }, [socket, handleSocketMessage]);

    const friendRequests = user?.data?.friends?.filter((friend: any) => friend.status === "pending" && friend.createdBy !== user?.data?.user.id);
    return (
        <>
            <div className="sm:p-4 sm:py-1 sm:w-screen max-w-32 sm:max-w-full sm:h-auto flex flex-col sm:flex-row sm:gap-2">
                <div 
                onWheel={handleWheel}
                className={`top-0 left-0 relative  sm:rounded-xl w-full bg-brand-top flex flex-col sm:flex-row shadow-2xl scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-gray-800 overflow-x-auto ${isOpen == 2 ? 'relative z-10 pb-8 sm:pb-0 min-w-20' : 'hidden sm:flex'}`} style={{ scrollbarWidth: "none" }}>
                    <ErrorBoundary>
                        <div className="flex items-center justify-center sm:border-r-2 sm:border-b-0 border-b-2 border-gray-600 bg-brand-top">
                            <div className="px-4 py-2 mb-2 sm:py-0 sm:mb-0 flex relative mt-2">
                                <Tippy content="Home" placement="right" arrow={true}>
                                    <Link to="/" key="/" className={`flex group items-center justify-center cursor-pointer h-12 w-12 hover:bg-brand-green hover:rounded-xl transition-all duration-200 ease-linear ${isActive("/")}`}>
                                        <img src={logo} alt="Logo" className="w-8 brightness-75 transition-all duration-300 group-hover:brightness-100 group-hover:scale-105 " draggable="false" />
                                    </Link>
                                </Tippy>

                                {friendRequests?.length != 0 && (
                                    <div className="absolute flex items-center justify-center right-0 top-0 w-4 h-4 bg-brand-green rounded-full">
                                        <p className="text-white text-xs font-semibold">
                                            {friendRequests?.length > 9 ? '9+' : friendRequests?.length}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-4 mt-2 mr-4 ml-4 items-center relative">
                            {userteams?.status != "success" && typeof unreadedMessages == "undefined" ? (
                                [...Array(13)].map((_, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-center cursor-pointer h-12 w-12 hover:bg-brand-green hover:rounded-xl transition-all duration-200 ease-linear bg-brand-back rounded-lg animate-pulse"
                                    >
                                        <div className="flex items-center justify-center w-16 h-12 rounded text-white text-x font-bold"></div>
                                    </div>
                                ))
                            ) : userteams.isError || userteams.data?.message ? (
                                <ErrorBox message={(userteams.error || userteams.data?.message)?.toString()} />
                            ) : (
                                <>
                                    {userteams?.data?.teams?.map((team: Team) => (
                                    <>
                                        <div key={team.id} className="relative flex w-full items-center justify-center">
                                            <TeamContext team={team} user={user?.data?.user}>
                                                {Object?.keys(unreadedMessages?.teamInfo?.find((teamc: any) => teamc.teamId == team.id)?.channelInfo || {}).length != 0 && (
                                                    <div className={`absolute flex items-center justify-center -mb-3 left-1/2 transform -translate-x-1/2 bottom-4 sm:bottom-0 z-10 w-2 h-4 sm:w-4 sm:h-2 bg-${window.location.pathname.startsWith(`/teams/${team.id}`) ? "brand-blue" : "white"
                                                        } sm:rounded-r-none sm:rounded-t-xl rounded-r-xl`} />
                                                )}
                                                {window.location.pathname.startsWith(`/teams/${team.id}`) && (
                                                    <div className="absolute flex items-center justify-center left-0 -ml-4 sm:-ml-0 sm:-mb-2 sm:bottom-0 sm:w-full sm:h-1 h-full w-1 bg-brand-green" />
                                                )}
                                                <div className={`flex relative items-center justify-center cursor-pointer hover:bg-brand-green hover:rounded-xl transition-all duration-200 ease-linear bg-brand-back rounded-lg`}>
                                                    <Tippy content={team.name} placement="right" arrow={true}>
                                                        <Link
                                                            to={`/teams/${team.id}`}
                                                            className={`flex items-center justify-center cursor-pointer h-12 w-12 hover:bg-brand-green hover:rounded-xl transition-all duration-200 ease-linear ${isActive(`/teams/${team.id}`)}`}
                                                            data-team-id={team.id}
                                                        >
                                                            {team.profilePicture ? (
                                                                <img
                                                                    src={team.profilePicture}
                                                                    alt={`${team.name} logo`}
                                                                    loading="lazy"
                                                                    className={`w-16 h-12 rounded-lg hover:rounded-xl transition-all duration-200 ease-linear ${isActive(`/teams/${team.id}`)}`}
                                                                />
                                                            ) : (
                                                                <div className="flex items-center justify-center w-16 h-12 rounded text-white text-x font-bold">
                                                                    {team.name.charAt(0).toUpperCase()}
                                                                </div>
                                                            )}
                                                        </Link>
                                                    </Tippy>
                                                    {Object?.keys(unreadedMessages?.teamInfo?.find((teamc: any) => teamc.teamId == team.id)?.badgingInfo || {}).length != 0 && (
                                                        <div className="absolute flex items-center justify-center right-0 bottom-0 w-4 h-4 bg-brand-green rounded-full">
                                                            <p className="text-white text-xs font-semibold">
                                                                {(() => {
                                                                    const unreadCount = Object.values(unreadedMessages?.teamInfo?.find((teamc: any) => teamc.teamId == team.id)?.badgingInfo || {}).reduce((a, b) => a + (b as any).unread, 0) as number;
                                                                    return unreadCount > 9 ? '9+' : unreadCount;
                                                                })()}
                                                            </p>
                                                        </div>
                                                    )}

                                                </div>
                                            </TeamContext>
                                        </div>
                                        </>
                                    ))}
                                </>
                            )}

                            {userteams?.data?.suspendedTeamLength != 0 && userteams?.data?.suspendedTeamLength != undefined && (
                                <Tippy content={`Suspended Teams (${userteams?.data?.suspendedTeamLength})`} placement="right" arrow={true}>
                                    <div className={`flex items-center justify-center mt-2 h-12 w-12 hover:bg-red-500 bg-red-500 transition-all duration-200 ease-linear ${isActive("/teams/suspended")}`}>
                                        <Icon name="shield" size="24" color="white" />
                                    </div>
                                </Tippy>
                            )}

                        </div>
                        <div className="flex gap-2 relative ml-4 sm:ml-0 mt-2 items-center border-gray-600">
                            <CreateTeam>
                                <Tippy content="Create Team" placement="right" arrow={true}>
                                    <div className="flex items-center justify-center cursor-pointer h-12 w-12 hover:bg-brand-green transition-all duration-200 ease-linear bg-brand-back rounded-xl">
                                        <Icon name="plus" size="24" color="white" />
                                    </div>
                                </Tippy>
                            </CreateTeam>
                        </div>
                    </ErrorBoundary>
                </div>

                <div className={`top-0 left-0 relative sm:rounded-xl max-w-20 min-w-20 flex shadow-2xl bg-brand-dark scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-gray-800 overflow-x-auto ${isOpen == 2 ? 'relative z-10 pb-8 sm:pb-0 min-w-20' : 'hidden sm:flex'}`} style={{ scrollbarWidth: "none" }}>
                    <ErrorBoundary>
                        <div className="flex items-center mt-4 border-gray-600">
                            <div className="px-4 flex gap-4 mb-4">

                            {unreadedMessages?.dmInfo && Object?.keys(unreadedMessages?.dmInfo || {}).length != 0 && (
                                    <>
                                        {Object.keys(unreadedMessages?.dmInfo || {}).map((dmId: string) => (
                                            <>
                                                {unreadedMessages?.dmInfo[dmId]?.unread != 0 && (
                                                    <div key={dmId} className="relative group flex items-center justify-center cursor-pointer h-12 w-12 hover:bg-brand-green hover:rounded-xl transition-all duration-200 ease-linear">
                                                        <Tippy content={Array.isArray(Object.values(unreadedMessages?.dmInfo?.[dmId]?.data?.participants)) ? Object.values(unreadedMessages?.dmInfo[dmId]?.data?.participants)?.find((participant: any) => participant.id !== user?.data?.user.id)?.username : ''} placement="right" arrow={true}>
                                                            <Link to={`/chat/${dmId}`} key={`/chat/${dmId}`} className={`relative flex items-center justify-center cursor-pointer h-12 w-12 hover:bg-brand-green hover:rounded-xl transition-all duration-200 ease-linear`}>
                                                                {Object.values(unreadedMessages?.dmInfo?.[dmId]?.data?.participants)?.find((participant: any) => participant.id !== user?.data?.user.id)?.profilePicture ? (
                                                                    <>
                                                                        <img
                                                                            src={Object.values(unreadedMessages?.dmInfo[dmId]?.data?.participants).find((participant: any) => participant.id !== user?.data?.user.id)?.profilePicture?.replace("webp", "png")}
                                                                            loading="lazy"
                                                                            className="w-16 h-12 rounded-lg hover:rounded-xl transition-all duration-200 ease-linear group-hover:hidden"
                                                                        />
                                                                        <img
                                                                            src={Object.values(unreadedMessages?.dmInfo[dmId]?.data?.participants).find((participant: any) => participant.id !== user?.data?.user.id)?.profilePicture}
                                                                            loading="lazy"
                                                                            className="w-16 h-12 rounded-lg hover:rounded-xl transition-all duration-200 ease-linear group-hover:block hidden"
                                                                        />
                                                                    </>
                                                                ) : (
                                                                    <div className="flex items-center justify-center w-16 h-12 rounded bg-brand-darkest text-white text-x font-bold">
                                                                        {Object.values(unreadedMessages?.dmInfo[dmId]?.data?.participants).find((participant: any) => participant.id !== user?.data?.user.id)?.username?.charAt(0).toUpperCase()}
                                                                    </div>
                                                                )}
                                                                {unreadedMessages?.dmInfo[dmId]?.unread != 0 && (
                                                                    <div className="absolute flex items-center justify-center right-0 bottom-0 w-4 h-4 bg-brand-green rounded-full">
                                                                        <p className="text-white text-xs font-semibold">
                                                                            {unreadedMessages?.dmInfo[dmId]?.unread > 9 ? '9+' : unreadedMessages?.dmInfo[dmId]?.unread}
                                                                        </p>
                                                                    </div>
                                                                )}
                                                            </Link>
                                                        </Tippy>
                                                    </div>
                                                )}
                                            </>
                                        ))}
                                    </>
                                )}

                                <Tippy content="Direct Messages" placement="right" arrow={true}>
                                    <Link to="/chat" key="/chat" className={`relative  flex items-center justify-center cursor-pointer h-12 w-12 hover:bg-brand-green hover:rounded-xl transition-all duration-200 ease-linear ${isActive("/chat")} bg-brand-dark`}>
                                        <Icon name="message" size="24" color="white" />
                                    </Link>
                                </Tippy>

                            </div>
                        </div>
                    </ErrorBoundary>  
                </div>
            </div>
        </>
    );
}
